'use client'

import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { HalloweenTrinket } from '@/components/halloween'

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'btn-sticker bg-brand-chartreuse-vivid font-marker text-lg text-black shadow-[4px_4px_0px_rgba(0,0,0,0.3)]',
        outline:
          'border-border bg-background hover:bg-muted hover:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-foreground dark:hover:bg-muted/50',
        destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 gap-1.5 px-2.5',
        xs: 'h-6 gap-1 px-2 text-xs',
        sm: 'h-7 gap-1 px-2.5 text-[0.8rem]',
        lg: 'h-9 gap-1.5 px-2.5',
        icon: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const ROUGH_FILTER_STYLE = { filter: 'url(#btn-rough)' } as const

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  children,
  style,
  ...props
}: ButtonProps) {
  const isRough = variant === 'default' || variant === null || variant === undefined
  const decorated =
    isRough ||
    (size === 'lg' && (variant === 'ghost' || variant === 'outline' || variant === 'secondary'))
  const filterStyle = isRough ? { ...style, ...ROUGH_FILTER_STYLE } : style
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }), decorated && 'spoopy-button')}
      style={filterStyle}
      {...props}
    >
      {asChild ? <Slottable>{children}</Slottable> : children}
      {decorated && (
        <HalloweenTrinket
          pool="button-bits"
          appearanceChance={0.65}
          canHide
          className="spoopy-button-charm"
        />
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
